import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Content override map: { content_type: { key: { field: value } } }.
 *  Fetched once at app startup (after auth) and re-fetched via reload() when an admin edits content. */
const ContentContext = createContext({ overrides: {}, reload: async () => {}, ready: false });

export function ContentProvider({ children }) {
  const { user } = useAuth();
  const [overrides, setOverrides] = useState({});
  const [ready, setReady] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/content/overrides");
      setOverrides(data || {});
    } catch { /* unauthenticated pages — silently skip */ }
    finally { setReady(true); }
  }, []);

  useEffect(() => {
    if (user && user !== false) reload();
    else if (user === false) setReady(true);
  }, [user, reload]);

  return <ContentContext.Provider value={{ overrides, reload, ready }}>{children}</ContentContext.Provider>;
}

export const useContent = () => useContext(ContentContext);

/** Read a single override with a code default fallback. */
export function useOverride(content_type, key, field, defaultValue = "") {
  const { overrides } = useContent();
  const v = overrides?.[content_type]?.[String(key)]?.[field];
  return v !== undefined && v !== null && v !== "" ? v : defaultValue;
}
